import React, { createContext, useContext, useCallback } from "react";

import { pushDataLayerWithClientBuildUnixTime } from "./ga";
import { createEcommerceEventItemName, DataLayerType } from "./gtmEventModel";

import type { EcommerceEventItem } from "./gtmEventModel";
import type { ReactNode } from "react";

/**
 * 単一アイテム配列型（例: add_to_wishlist など、正確に1つのアイテムが必要なイベント用）
 */
// satisfiesに使用しているが、誤検知されるためエラーを無視する
// eslint-disable-next-line unused-imports/no-unused-vars
type EcommerceEventSingleItem = [EcommerceEventItem];

/**
 * アナリティクス ecommerce イベント用のアイテムデータ。
 * この抽象化により、親コンポーネントは特定のアナリティクスイベントがどのようにトリガーされるかを
 * 知らなくてもアイテムコンテキストを提供できます。
 */
export type AnalyticsEventItem = {
  eventId: string;
  eventGoodsFirestoreId: string;
  eventGoodsId?: string;
  itemName: string;
  price: number;
  itemVariant?: string;
};

/**
 * アイテムパラメータを使用する既知の GA4 ecommerce イベント。
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?hl=ja&client_type=gtag
 * このリストには推奨e-commerceイベントが含めていますが、`AnalyticsEventAction`型に追加することでカスタムイベントもサポートします。
 */
export type KnownEcommerceEventAction =
  | "add_to_cart"
  | "add_to_wishlist"
  | "begin_checkout"
  | "view_item"
  | "view_item_list"
  | "select_item"
  | "add_payment_info"
  | "add_shipping_info"
  | "purchase"
  | "refund"
  | "remove_from_cart";

/**
 * アナリティクスイベントアクション型。
 * 既知の GA4 推奨イベントとカスタムイベント名の両方をサポートします。
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?hl=ja&client_type=gtag
 */
export type AnalyticsEventAction = KnownEcommerceEventAction;

/**
 * アナリティクスイベントのオーバーライドハンドラ。
 * 提供されたアイテムデータを使用して、既知またはカスタムの任意のイベントアクションを処理できます。
 */
export type AnalyticsEventHandler = (
  action: AnalyticsEventAction,
  item: AnalyticsEventItem
) => void | Promise<void>;

/**
 * アナリティクスコンテキストの値。
 * コンテキストが提供される場合、アイテムは常に存在します（null になることはありません）。
 */
type AnalyticsContextValue = {
  /**
   * コンテキスト内の現在のアイテムデータ
   * 常に存在 - プロバイダーは null 以外のアイテムを要求します
   */
  item: AnalyticsEventItem;

  /**
   * 現在のアイテムデータでアナリティクスイベントを送信します。
   * 任意のイベントアクション（GA4 推奨イベントまたはカスタムイベント）をサポートします。
   * @param action - アナリティクスイベントアクション型（カスタムイベントの場合は任意の文字列）
   * @param overrideHandler - カスタムイベント処理用のオプションのオーバーライドハンドラ
   */
  sendEvent: (
    action: AnalyticsEventAction,
    overrideHandler?: AnalyticsEventHandler
  ) => void;
};

/**
 * イベント追跡用のアナリティクスコンテキスト
 * プロバイダーの外部で使用される場合、コンテキストの値は null ですが、
 * プロバイダーの内部ではアイテムが null になることはありません。
 */
const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

/**
 * アナリティクスコンテキストプロバイダーのプロップス
 */
type AnalyticsProviderProps = {
  /**
   * コンテキストで提供するアイテムデータ
   */
  item: AnalyticsEventItem;

  /**
   * すべてのイベントに適用されるオプションのデフォルトイベントハンドラ
   */
  defaultEventHandler?: AnalyticsEventHandler;

  children: ReactNode;
};

/**
 * アナリティクスイベントアイテムデータから ecommerce イベントアイテムを作成します。
 */
const createEcommerceEventItem = (
  item: AnalyticsEventItem
): EcommerceEventItem => {
  return {
    item_category: "goods",
    item_id: item.eventGoodsFirestoreId,
    item_name: createEcommerceEventItemName({
      itemName: item.itemName,
      eventId: item.eventId,
    }),
    item_list_id: item.eventId,
    price: item.price,
    quantity: 1,
    ...(item.itemVariant !== undefined
      ? { item_variant: item.itemVariant }
      : {}),
  };
};

/**
 * リクエストビルダー関数型。
 * アイテムデータを受け取り、適切な DataLayerType イベント構造を作成します。
 */
type EventRequestBuilder = (
  item: AnalyticsEventItem,
  ecommerceItem: EcommerceEventItem
) => DataLayerType;

/**
 * トップレベルに eid を持つイベントを作成します（例: add_to_wishlist, add_to_cart）。
 * ecommerce データと共にトップレベルで eventId が必要なイベントを構築します。
 */
const createEventWithEid: EventRequestBuilder = (item, ecommerceItem) => {
  // 返却構造は EventAddToWishlist | EventAddToCart と一致します
  return {
    event: "add_to_wishlist" as const, // プレースホルダー - 実際のアクションで上書きされます
    eid: item.eventId,
    ecommerce: {
      currency: "JPY" as const,
      value: item.price,
      items: [ecommerceItem] satisfies EcommerceEventSingleItem,
    },
  } satisfies DataLayerType;
};

/**
 * ecommerce 内に transaction_id を持つイベントを作成します（例: purchase）。
 * 責務: ecommerce オブジェクト内で transaction_id が必要な購入イベントを構築します。
 */
const createEventWithTransactionId: EventRequestBuilder = (
  item,
  ecommerceItem
) => {
  // 返却構造は EventPurchase と一致します
  return {
    event: "purchase" as const, // プレースホルダー - 実際のアクションで上書きされます
    ecommerce: {
      currency: "JPY" as const,
      value: item.price,
      transaction_id: item.eventId, // eventId を transaction_id として使用
      items: [ecommerceItem],
    },
  } satisfies DataLayerType;
};

/**
 * 汎用 ecommerce 構造を持つイベントを作成します（例: view_item, begin_checkout）。
 * 責務: トップレベルの eid なしで ecommerce データのみが必要な標準的な ecommerce イベントを構築します。
 */
const createEventWithEcommerce: EventRequestBuilder = (item, ecommerceItem) => {
  // 返却構造は EventViewItem | EventBeginCheckout | などと一致します
  return {
    event: "view_item" as const, // プレースホルダー - 実際のアクションで上書きされます
    ecommerce: {
      currency: "JPY" as const,
      value: item.price,
      items: [ecommerceItem],
    },
  } satisfies DataLayerType;
};

/**
 * イベント設定マップ。
 * アクション名をリクエストビルダー関数にリンクします。
 * 各エントリは以下を定義します:
 * - action-name: アナリティクスイベントアクション文字列
 * - builder: 適切なリクエスト構造を作成する責任を持つ関数
 *
 * このマップベースのアプローチにより、ハードコードされた if 条件が排除され、
 * 各イベントタイプの構造化方法の単一の情報源が提供されます。
 */
const EVENT_CONFIG_MAP: Record<AnalyticsEventAction, EventRequestBuilder> = {
  add_to_cart: createEventWithEid,
  add_to_wishlist: createEventWithEid,
  begin_checkout: createEventWithEcommerce,
  view_item: createEventWithEcommerce,
  view_item_list: createEventWithEcommerce,
  select_item: createEventWithEcommerce,
  add_payment_info: createEventWithEcommerce,
  add_shipping_info: createEventWithEcommerce,
  purchase: createEventWithTransactionId,
  refund: createEventWithTransactionId,
  remove_from_cart: createEventWithEcommerce,
};

/**
 * アイテムコンテキストを持つアナリティクスイベントの汎用ハンドラ。
 * イベント設定マップを使用して、適切なリクエスト構造を決定します。
 *
 * 責務の流れ:
 * 1. EVENT_CONFIG_MAP からビルダーを取得（またはデフォルトで createEventWithEcommerce）
 * 2. ビルダーがイベントタイプ要件に基づいてリクエスト構造を作成
 * 3. プレースホルダーイベント名を実際のアクション名で上書き
 * 4. データレイヤーにプッシュ
 *
 * GA4 推奨イベントの完全なリストと任意のカスタムイベント名をサポートします。
 * @see https://developers.google.com/analytics/devguides/collection/ga4/reference/events?hl=ja&client_type=gtag
 */
const handleAnalyticsEvent = (
  action: AnalyticsEventAction,
  item: AnalyticsEventItem
): void => {
  const ecommerceItem = createEcommerceEventItem(item);

  // 設定マップから適切なビルダーを取得
  // 不明なアクションの場合は createEventWithEcommerce をデフォルトとして使用（カスタムイベントをサポート）
  const builder = EVENT_CONFIG_MAP[action] ?? createEventWithEcommerce;

  // 正しい構造でイベントリクエストを構築
  const eventData = builder(item, ecommerceItem);

  // プレースホルダーイベント名を実際のアクション名で上書き
  // DataLayerType は判別共用体であるため、ここで型アサーションが必要です
  // TypeScript はコンパイル時に構造がアクションと一致することを検証できません
  const finalEventData: DataLayerType = {
    ...eventData,
    event: action,
  } as DataLayerType;

  pushDataLayerWithClientBuildUnixTime(finalEventData);
};

/**
 * アナリティクスコンテキストプロバイダー
 *
 * 子コンポーネントにアイテムコンテキストとイベント送信機能を提供します。
 * 子コンポーネントは現在のアイテムデータを使用してアナリティクスイベントを送信できます。
 *
 * @example
 * ```tsx
 * <AnalyticsProvider item={itemData}>
 *   <WatchlistIconButton />
 * </AnalyticsProvider>
 * ```
 */
export const AnalyticsProvider: React.FC<AnalyticsProviderProps> = ({
  item,
  defaultEventHandler = handleAnalyticsEvent,
  children,
}) => {
  const sendEvent = useCallback<AnalyticsContextValue["sendEvent"]>(
    (action, overrideHandler) => {
      // オーバーライドハンドラが提供されている場合はそれを使用、そうでなければデフォルトハンドラを使用
      const handler = overrideHandler ?? defaultEventHandler;

      void handler(action, item);
    },
    [item, defaultEventHandler]
  );

  const value: AnalyticsContextValue = {
    item,
    sendEvent,
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * アナリティクスコンテキストを使用するフック
 *
 * @throws AnalyticsProvider の外部で使用された場合にエラーをスローします
 *
 * @example
 * ```tsx
 * const { item, sendEvent } = useAnalytics();
 *
 * // GA4 推奨イベント
 * sendEvent("add_to_wishlist");
 *
 * // カスタムイベント
 * sendEvent("custom_item_viewed", (action, item) => {
 *   // 非標準イベント構造用のカスタムハンドラ
 * });
 * ```
 */
export const useAnalytics = (): AnalyticsContextValue => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error("useAnalytics must be used within an AnalyticsProvider");
  }
  return context;
};

/**
 * アナリティクスコンテキストを使用するフック（オプション - プロバイダー内にない場合は null を返します）
 *
 * @example
 * ```tsx
 * const analytics = useAnalyticsOptional();
 *
 * const handleClick = () => {
 *   // GA4 推奨イベントまたはカスタムイベント
 *   analytics?.sendEvent("add_to_wishlist");
 *   // カスタムイベントもサポートされています
 *   analytics?.sendEvent("custom_item_interaction");
 * };
 * ```
 */
export const useAnalyticsOptional = (): AnalyticsContextValue | null => {
  return useContext(AnalyticsContext);
};
